import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../../ui/Modal';
import { Link, useNavigate, useParams } from 'react-router';
import { useAppUser } from '../../auth/AppUserContext';
import { canConductAudits, canSeeStore, equalsIgnoreCase } from '../../data/scope';
import {
  allowedLabel,
  EMPTY_ALLOWLIST,
  isAllowedEmail,
  isInternalEmail,
  MAX_NOTIFY_EXTRA,
  type EmailAllowlist,
} from '../../data/recipients';
import { newClientKey, SubmissionKey } from '../../data/clientKey';
import { isPersonName, NAME_REQUIRED_MESSAGE } from '../../data/typedName';
import { OfflineError } from '../../api/offline';
import { getTaskOutbox } from '../../local';
import { OutboxList } from '../../ui/tasks/OutboxList';
import { useTaskOutbox } from '../../ui/useTaskOutbox';
import { latestReopen } from '../../data/reopenReason';
import {
  canUndoCompletion,
  easternIsoDay,
  extensionDatePassed,
  EXTENSION_REASON_MAX,
  extensionDateProblem,
  hasProofSince,
  MAX_EXTENSION_DAYS,
  PROOF_REQUIRED_MESSAGE,
} from '../../data/taskRules';
import { completesOnStoreBehalf, ON_BEHALF_CONFIRM } from '../../ui/tasks/onBehalf';
import { ReopenReason } from '../../ui/tasks/ReopenReason';
import { EventText } from '../../ui/tasks/EventText';
import { getEmailAllowlist } from '../../data/emailAllowlist';
import { getAllStores, getVisibleStores } from '../../data/stores';
import {
  acceptMoreTime,
  askForMoreTime,
  completeTask,
  declineMoreTime,
  deleteTask,
  getTaskById,
  reassignTask,
  setProofRequired,
  undoCompletion,
  updateTaskDueDate,
  type ServerTask,
} from '../../data/tasks';
import {
  addTaskComment,
  addTaskEvent,
  getTaskThread,
  type TaskThreadEntry,
} from '../../data/taskComments';
import { getUserDisplayName } from '../../graph/directory';
import { PersonName, usePersonLabel } from '../../ui/PersonName';
import {
  getAttachmentsForTask,
  prepareTaskFile,
  uploadPreparedTaskFile,
  type PreparedTaskFile,
  type TaskAttachmentRow,
} from '../../data/taskAttachments';
import { formatDateTime } from '../../ui/dates';
import { downloadAuditAttachment } from '../../data/audits';
import type { AppUser, Store } from '../../data/types';
import { ActionButton, useActionLock } from '../../ui/ActionButton';
import { AssigneeField } from '../../ui/AssigneeField';
import { AttachmentChip } from '../../ui/AttachmentChip';
import { PrincipalField } from '../../ui/PrincipalField';
import { TaskRow } from '../../ui/tasks/TaskRow';
import { ReopenTaskModal } from '../../ui/tasks/ReopenTaskModal';
import { formatDay } from '../../ui/dates';
import { dataViaApi } from '../../api/mode';
import { getTaskReminders, type ReminderView } from '../../data/reminders';
import { nextTaskSteps } from '../../data/reminderSchedule';
import { taskStepsText } from '../../ui/reminderText';
import { ReminderHistory } from '../../ui/ReminderHistory';
import { daysUntil } from '../../audit/windows';
import { shortAddress } from '../../ui/people';

/**
 * One task on its own page — the ticket view and the landing spot for
 * notification-email links (docs/flows.md): the task card, comments and
 * history in one timeline, file uploads, and (for conduct roles) reassign
 * and due-date controls. Visibility matches the Tasks hub rule: a personal
 * assignment is always visible to its assignee; anything else follows
 * store scope. Comments and files queue offline (B4); completing needs
 * signal.
 */

// The typed name behind a shared store account is NEVER remembered — not
// in storage, not across tasks or dialogs, and not after it has been used
// (owner ruling 2026-10-26: two people can use one store device in a day,
// so the next person must never find the last one's name filled in).
/** B4 (owner rulings 2026-10-25): no signal → comments/files wait on the device; completing needs signal. */
const QUEUED_NOTE = 'No signal — saved on this device. It sends automatically when you’re back online (keep AuditPlus open).';
const OFFLINE_COMPLETE_MESSAGE =
  'You’re offline — completing a task needs signal. Try again when you’re back online (photos and comments can be added now; they send later).';

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { user } = useAppUser();
  const [task, setTask] = useState<ServerTask | null | 'missing'>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [thread, setThread] = useState<TaskThreadEntry[] | null>(null);
  const [files, setFiles] = useState<TaskAttachmentRow[] | null>(null);
  const [creatorName, setCreatorName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reopening, setReopening] = useState(false);

  const id = Number(taskId);

  /** Resolves once the task is re-read — actions that change it await it (one tap = one action). */
  const refreshTask = (): Promise<void> =>
    getTaskById(id)
      .then((t) => setTask(t ?? 'missing'))
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  /**
   * After a comment or file lands: the server stamps LastActivityAt, so the
   * card shows "today" at once and then re-reads (DM round 2 F8 — it said
   * "25 days ago" until a reload).
   */
  const touchTask = () => {
    const now = new Date().toISOString();
    setTask((t) => (t && t !== 'missing' ? { ...t, lastActivityAt: now } : t));
    refreshTask();
  };
  const refreshThread = (): Promise<void> =>
    getTaskThread(id)
      .then(setThread)
      .catch(() => setThread([]));
  const refreshFiles = () => {
    getAttachmentsForTask(id)
      .then(setFiles)
      .catch(() => setFiles([]));
  };

  useEffect(() => {
    if (!Number.isInteger(id) || id <= 0) {
      setTask('missing');
      return;
    }
    refreshTask();
    refreshThread();
    refreshFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // Include inactive stores so a divested store's task still shows a name.
    getAllStores({ includeInactive: true })
      .then(setStores)
      .catch(() => setStores([]));
  }, []);

  // Creator display name: stored at creation for newer tasks; older rows
  // fall back to a best-effort directory lookup, then the UPN itself.
  const createdBy = task && task !== 'missing' ? task.createdBy : '';
  const storedCreatorName = task && task !== 'missing' ? task.createdByName : '';
  useEffect(() => {
    if (!createdBy) return;
    if (storedCreatorName) {
      setCreatorName(storedCreatorName);
      return;
    }
    let cancelled = false;
    void getUserDisplayName(createdBy).then((name) => {
      if (!cancelled && name) setCreatorName(name);
    });
    return () => {
      cancelled = true;
    };
  }, [createdBy, storedCreatorName]);

  const store =
    task && task !== 'missing'
      ? (stores.find((s) => Number(s.id) === task.storeId) ?? null)
      : null;

  // Only a failed LOAD replaces the page; a failed action (reopen, reassign,
  // due date, delete) shows above the task with a way to dismiss it.
  if (error && (task === null || task === 'missing')) {
    return (
      <div className="page-message">
        <p className="error">{error}</p>
        <Link to="/tasks">Back to tasks</Link>
      </div>
    );
  }
  if (task === 'missing') {
    return (
      <div className="page-message">
        <p>This task doesn’t exist or is outside your access — it may have been deleted.</p>
        <Link to="/tasks">Back to tasks</Link>
      </div>
    );
  }
  if (task === null || !user) {
    return <div className="page-message">Loading task…</div>;
  }

  const isAssignee = equalsIgnoreCase(task.assignedTo, user.upn);
  const accessible = isAssignee || (store !== null && canSeeStore(user, store));
  if (stores.length > 0 && !accessible) {
    return (
      <div className="page-message">
        <p>This task is outside your access.</p>
        <Link to="/tasks">Back to tasks</Link>
      </div>
    );
  }

  const canManage = canConductAudits(user.role);
  const view = task.status === 'complete' ? 'completed' : 'open';

  return (
    <div>
      <p className="back-link-row">
        <Link className="entity-link" to="/tasks">
          ← Back to all tasks
        </Link>
      </p>
      {error && (
        <p className="error" role="alert">
          {error}{' '}
          <button className="btn btn-small" onClick={() => setError(null)}>
            Dismiss
          </button>
        </p>
      )}
      <div className="page-head">
        <h1>Task</h1>
        {canManage && (
          <ActionButton
            className="btn btn-danger"
            disabled={busy}
            busyLabel="Deleting…"
            onAction={async () => {
              if (
                !window.confirm(
                  `Delete “${task.title}”?\n\nThis permanently deletes the task ` +
                    'and all of its comments, history and uploaded files. ' +
                    'This can’t be undone.',
                )
              ) {
                return;
              }
              setBusy(true);
              try {
                await deleteTask(task.id);
                navigate('/tasks');
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
                setBusy(false);
              }
            }}
          >
            Delete task
          </ActionButton>
        )}
      </div>
      <TaskRow
        // The reopen reason tops the page (owner ruling 2026-10-25): the
        // thread's once loaded (same source as the completion box — never two
        // different reasons on one page; QA 2026-10-25), the server's copy before.
        task={task.status === 'reopened' && thread !== null ? { ...task, reopenNote: latestReopen(thread) } : task}
        store={store ?? undefined}
        view={view}
        canManage={canManage}
        busy={busy}
        showOpen={false}
        noActivity={thread !== null && thread.length === 0}
        // No Complete button here — completing happens in the composer
        // below, where the required completion comment lives.
        onReopen={() => setReopening(true)}
      />
      {reopening && (
        <ReopenTaskModal
          task={task}
          onClose={() => setReopening(false)}
          onDone={() => {
            setReopening(false);
            refreshTask();
            refreshThread();
          }}
        />
      )}
      {/* B3 (owner rulings 2026-10-25): a store undoes its own completion — else ask the DM. */}
      {user.role === 'StoreTeam' && task.status === 'complete' &&
        (canUndoCompletion({ ...task, completedByUpn: task.completedByUpn ?? '' }, user, new Date()) ? (
          <UndoCompletion
            // Never the same key as a sibling (ThreadSection used task.id too): two siblings with one
            // key made React draw "Undo completion" three times after a refresh (UI review U189).
            key={`undo-${task.id}`}
            task={task}
            user={user}
            onDone={() => {
              refreshTask();
              refreshThread();
            }}
          />
        ) : (
          <p className="muted">Need to change something? Ask your DM to reopen it.</p>
        ))}
      {/* B2 (owner rulings 2026-10-25): the store asks for more time; a DM/Auditor/Admin answers. */}
      <MoreTime
        task={task}
        user={user}
        store={store}
        onChanged={async () => {
          await Promise.all([refreshTask(), refreshThread()]);
        }}
      />
      {task.auditId !== null && (
        <p className="muted">
          <Link to={`/audits/records/${task.auditId}`}>
            View the audit this task came from
          </Link>
        </p>
      )}

      {canManage && (
        <ManagePanel
          task={task}
          user={user}
          onChanged={async () => {
            refreshThread();
            await refreshTask();
          }}
          onError={setError}
        />
      )}

      <FilesSection files={files} />

      {/* Re-read when the due date, assignee or status changes (best effort). */}
      <TaskReminders key={`${task.id}|${task.dueDate}|${task.assignedTo}|${task.status}`} task={task} storeActive={store?.active !== false} />

      {/* key: a held file or draft never follows the user to another task (QA 2026-10-25). */}
      <ThreadSection
        key={`thread-${task.id}`}
        task={task}
        store={store}
        user={user}
        creatorName={creatorName}
        thread={thread}
        files={files}
        onPosted={() => {
          refreshThread();
          touchTask();
        }}
        onTaskChanged={refreshTask}
        onUploaded={() => {
          refreshFiles();
          refreshThread();
          touchTask();
        }}
      />
    </div>
  );
}

/** Reassign + due date, conduct roles only. Both write timeline events. */
function ManagePanel({
  task,
  user,
  onChanged,
  onError,
}: {
  task: ServerTask;
  user: AppUser;
  onChanged: () => void | Promise<void>;
  onError: (message: string) => void;
}) {
  const [assignee, setAssignee] = useState(task.assignedTo);
  const [dueDate, setDueDate] = useState(task.dueDate);
  const lock = useActionLock();
  const saving = lock.busy;

  const [proofOn, setProofOn] = useState(task.proofRequired === true);
  // Follow external changes (e.g. after a refresh).
  useEffect(() => setProofOn(task.proofRequired === true), [task.proofRequired]);
  useEffect(() => setAssignee(task.assignedTo), [task.assignedTo]);
  useEffect(() => setDueDate(task.dueDate), [task.dueDate]);

  const actor = { upn: user.upn, name: user.displayName };
  const assigneeChanged =
    assignee.trim() !== '' && !equalsIgnoreCase(assignee, task.assignedTo);
  const dueChanged = dueDate !== '' && dueDate !== task.dueDate;
  // A changed due date is today (Eastern) or later — the picker's min doesn't stop typing (final-build rule, 2026-10-26).
  const dueOk = /^\d{4}-\d{2}-\d{2}$/.test(dueDate) && dueDate >= easternIsoDay(new Date());

  const save = async () => {
    // Both close the store's pending "more time" request (B2; the server
    // clears it) — say so first (owner ruling 2026-10-25 for reassigning).
    if (task.extensionRequest && task.status !== 'complete' && !window.confirm(
      assigneeChanged && dueChanged
        ? 'This also closes the store’s request for more time (the date it asked for is not used). Save anyway?'
        : assigneeChanged
          ? 'This also closes the store’s request for more time. Reassign anyway?'
          : 'This also answers the store’s request for more time (the date it asked for is replaced). Change the due date anyway?',
    )) return;
    try {
      // Due date FIRST: after a reassignment the person saving may no longer
      // be allowed to see the task (it was theirs only by assignment).
      if (dueChanged) await updateTaskDueDate(task, dueDate, actor);
      if (assigneeChanged) await reassignTask(task, assignee.trim(), actor);
      await onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="task-manage">
      {/* B1 (2026-10-25): switchable while the task is open. */}
      {task.status !== 'complete' && (
        <label className="checkbox checkbox-inline">
          <input
            type="checkbox"
            checked={proofOn}
            disabled={saving}
            onChange={(e) => {
              const on = e.target.checked;
              setProofOn(on); // shows at once; the reload below confirms it
              void lock.run(async () => {
                try {
                  await setProofRequired(task.id, on);
                  await onChanged();
                } catch (err) {
                  setProofOn(!on);
                  onError(err instanceof Error ? err.message : String(err));
                }
              });
            }}
          />
          Proof required (photo or PDF to complete)
        </label>
      )}
      <div className="form-grid">
        <AssigneeField
          label="Assigned to"
          value={assignee}
          onChange={setAssignee}
        />
        <label>
          Due date
          <input
            type="date"
            min={easternIsoDay(new Date())}
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            aria-invalid={dueChanged && !dueOk ? true : undefined}
          />
          {dueChanged && !dueOk && <span className="error" style={{ fontSize: '0.85rem' }}>Pick today or a later day.</span>}
        </label>
      </div>
      {(assigneeChanged || dueChanged) && (
        <div className="modal-actions" style={{ marginTop: '0.5rem' }}>
          <button
            className="btn"
            disabled={saving}
            onClick={() => {
              setAssignee(task.assignedTo);
              setDueDate(task.dueDate);
            }}
          >
            Discard
          </button>
          <ActionButton
            className="btn btn-primary"
            lock={lock}
            busyLabel={assigneeChanged && !dueChanged ? 'Reassigning…' : 'Saving…'}
            disabled={dueChanged && !dueOk}
            onAction={save}
          >
            {assigneeChanged && dueChanged
              ? 'Save changes'
              : assigneeChanged
                ? 'Reassign'
                : 'Change due date'}
          </ActionButton>
        </div>
      )}
    </div>
  );
}

/**
 * "Undo completion" for the store that completed its own task (B3, owner
 * rulings 2026-10-25): until the end of the next day, with a reason; the due
 * date stays. A shared store account names the person (the reopen event is
 * signed like a comment — the server refuses it otherwise).
 */
function UndoCompletion({ task, user, onDone }: { task: ServerTask; user: AppUser; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [name, setName] = useState('');
  const [tried, setTried] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const lock = useActionLock();
  const nameOk = isPersonName(name, user);
  // Closing (done or cancelled) forgets what was typed — the next person on
  // a shared device starts blank (owner ruling 2026-10-26).
  const close = () => {
    setOpen(false);
    setName('');
    setReason('');
    setTried(false);
    setSaveError(null);
  };
  if (!open) {
    return (
      <p>
        <button type="button" className="btn" onClick={() => setOpen(true)}>
          Undo completion
        </button>{' '}
        <span className="muted button-hint">
          Undo is available until the end of {undoLastDay(task.completedAt)}. Your due date stays the same.
        </span>
      </p>
    );
  }
  return (
    <Modal onClose={close} busy={lock.busy} dirty={reason.trim() !== ''}>
        <h2>Undo completion</h2>
        <p>{task.title}</p>
        <label style={{ display: 'block' }}>
          Your name
          <input
            value={name}
            maxLength={100}
            className={nameOk || !tried ? undefined : 'input-required'}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            placeholder="First and last name"
          />
          {tried && !nameOk && (
            <span className="error" style={{ fontSize: '0.85rem' }}>
              {NAME_REQUIRED_MESSAGE}
            </span>
          )}
        </label>
        <label style={{ display: 'block', marginTop: '0.5rem' }}>
          Why are you undoing it? (required)
          <textarea
            className={`q-input${tried && !reason.trim() ? ' input-required' : ''}`}
            rows={3}
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What still needs to be done?"
          />
          {tried && !reason.trim() && (
            <span className="error" style={{ fontSize: '0.85rem' }}>
              Say why you are undoing it.
            </span>
          )}
        </label>
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          The task goes back to open with its original due date ({task.dueDate ? formatDay(task.dueDate) : 'not set'}).
        </p>
        {saveError && (
          <p className="error" role="alert">
            {saveError}
          </p>
        )}
        <div className="modal-actions">
          <button className="btn" disabled={lock.busy} onClick={() => close()}>
            Cancel
          </button>
          <ActionButton
            className="btn btn-primary"
            lock={lock}
            busyLabel="Undoing…"
            onAction={async () => {
              setSaveError(null);
              if (!nameOk || !reason.trim()) {
                setTried(true);
                return;
              }
              try {
                await undoCompletion(task, { upn: user.upn, name: name.trim() }, reason);
              } catch (err) {
                setSaveError(err instanceof Error ? err.message : String(err));
                return;
              }
              close();
              onDone();
            }}
          >
            Undo completion
          </ActionButton>
        </div>
    </Modal>
  );
}

/** YYYY-MM-DD + n days (calendar arithmetic on the date itself). */
function addDays(iso: string, n: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

/**
 * "Ask for more time" (B2, owner rulings 2026-10-25). The task's own store
 * asks for a new due date with a reason; the due date only moves when a DM,
 * Auditor or Admin accepts (lateness keeps counting until then). Both the
 * request and the answer are comments too, so Flow 2c emails them.
 */
function MoreTime({
  task,
  user,
  store,
  onChanged,
}: {
  task: ServerTask;
  user: AppUser;
  store: Store | null;
  onChanged: () => Promise<void>;
}) {
  const request = task.status === 'complete' ? null : (task.extensionRequest ?? null);
  // Accepting would set a past due date (QA 2026-10-25; the server refuses it too).
  const passed = request ? extensionDatePassed(request.date, new Date()) : false;
  const [asking, setAsking] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useActionLock();
  const [acceptKey] = useState(() => new SubmissionKey('cmt'));
  const answers = canConductAudits(user.role);
  const ownStoreTask = user.role === 'StoreTeam' && task.status !== 'complete' && equalsIgnoreCase(task.assignedTo, user.upn);

  if (request) {
    return (
      <div className="more-time-banner" role="status">
        <p style={{ margin: 0 }}>
          <strong>{request.name || 'The store'}</strong> asked for more time: until{' '}
          <strong>{formatDay(request.date)}</strong> — “{request.reason}”
          {request.at && <span className="muted nowrap"> (asked {formatDay(request.at)})</span>}
        </p>
        {user.role === 'StoreTeam' && (
          <p className="muted" style={{ margin: '0.35rem 0 0' }}>
            Waiting for your DM’s answer. Your due date stays {task.dueDate ? formatDay(task.dueDate) : 'as it is'} until then.
          </p>
        )}
        {answers && passed && (
          <p className="muted" style={{ margin: '0.35rem 0 0' }}>
            That date has passed — decline it, or change the due date below.
          </p>
        )}
        {answers && (
          <div className="modal-actions" style={{ marginTop: '0.5rem', justifyContent: 'flex-start' }}>
            {!passed && (
            <ActionButton
              className="btn btn-primary"
              lock={lock}
              busyLabel="Accepting…"
              onAction={async () => {
                setError(null);
                try {
                  await acceptMoreTime(task, { upn: user.upn, name: user.displayName }, acceptKey.for(`${task.id}\n${request.date}`));
                  acceptKey.reset();
                } catch (err) {
                  setError(err instanceof Error ? err.message : String(err));
                }
                await onChanged();
              }}
            >
              Accept new date
            </ActionButton>
            )}
            <button type="button" className="btn" disabled={lock.busy} onClick={() => setDeclining(true)}>
              Decline…
            </button>
          </div>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {declining && (
          <DeclineMoreTime
            task={task}
            user={user}
            onClose={() => setDeclining(false)}
            onDone={async () => {
              setDeclining(false);
              await onChanged();
            }}
          />
        )}
      </div>
    );
  }
  if (!ownStoreTask) return null;
  return (
    <>
      <p>
        <button type="button" className="btn" onClick={() => setAsking(true)}>
          Ask for more time
        </button>{' '}
        <span className="muted button-hint">
          Your DM decides. Until then the due date stays the same.
        </span>
      </p>
      {asking && (
        <AskMoreTime
          task={task}
          user={user}
          store={store}
          onClose={() => setAsking(false)}
          onDone={async () => {
            setAsking(false);
            await onChanged();
          }}
        />
      )}
    </>
  );
}

function AskMoreTime({
  task,
  user,
  store,
  onClose,
  onDone,
}: {
  task: ServerTask;
  user: AppUser;
  store: Store | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  // Eastern days, like the rule the server checks (QA 2026-10-25: a browser
  // west of Eastern offered a day the server refuses late at night).
  const today = easternIsoDay(new Date());
  const due = task.dueDate && task.dueDate > today ? task.dueDate : today;
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [name, setName] = useState('');
  const [tried, setTried] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const lock = useActionLock();
  const [key] = useState(() => new SubmissionKey('cmt'));
  const nameOk = isPersonName(name, user);
  const dateProblem = extensionDateProblem(date, task.dueDate, new Date());
  // The store's DM always hears about it (owner ruling 2026-10-25), besides
  // the creator Flow 2c emails by default.
  // Not when the DM created the task — Flow 2c already emails the creator.
  const dm =
    store && isInternalEmail(store.dmUpn) && !equalsIgnoreCase(store.dmUpn, user.upn) && !equalsIgnoreCase(store.dmUpn, task.createdBy)
      ? [store.dmUpn.trim()]
      : [];
  return (
    <Modal onClose={onClose} busy={lock.busy} dirty={reason.trim() !== ''}>
        <h2>Ask for more time</h2>
        <p>{task.title}</p>
        <label style={{ display: 'block' }}>
          Your name
          <input
            value={name}
            maxLength={100}
            className={nameOk || !tried ? undefined : 'input-required'}
            onChange={(e) => setName(e.target.value)}
            autoComplete="off"
            placeholder="First and last name"
          />
          {tried && !nameOk && (
            <span className="error" style={{ fontSize: '0.85rem' }}>
              {NAME_REQUIRED_MESSAGE}
            </span>
          )}
        </label>
        <label style={{ display: 'block', marginTop: '0.5rem' }}>
          New due date
          <input
            type="date"
            value={date}
            min={addDays(due, 1)}
            max={addDays(due, MAX_EXTENSION_DAYS)}
            className={tried && dateProblem ? 'input-required' : undefined}
            onChange={(e) => setDate(e.target.value)}
          />
          {tried && dateProblem && (
            <span className="error" style={{ fontSize: '0.85rem' }}>
              {dateProblem}
            </span>
          )}
        </label>
        <label style={{ display: 'block', marginTop: '0.5rem' }}>
          Why do you need more time? (required)
          <textarea
            className={`q-input${tried && !reason.trim() ? ' input-required' : ''}`}
            rows={3}
            maxLength={EXTENSION_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. The part is on order — arrives next Tuesday."
          />
          {tried && !reason.trim() && (
            <span className="error" style={{ fontSize: '0.85rem' }}>
              Say why you need more time.
            </span>
          )}
        </label>
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          Current due date: {task.dueDate ? formatDay(task.dueDate) : 'not set'}. It only changes if your DM accepts — up to {MAX_EXTENSION_DAYS} days more.
        </p>
        {saveError && (
          <p className="error" role="alert">
            {saveError}
          </p>
        )}
        <div className="modal-actions">
          <button className="btn" disabled={lock.busy} onClick={onClose}>
            Cancel
          </button>
          <ActionButton
            className="btn btn-primary"
            lock={lock}
            busyLabel="Sending…"
            onAction={async () => {
              setSaveError(null);
              if (!nameOk || dateProblem || !reason.trim()) {
                setTried(true);
                return;
              }
              try {
                await askForMoreTime(
                  task,
                  { date, reason, name: name.trim() },
                  { upn: user.upn, name: name.trim() },
                  dm,
                  key.for(JSON.stringify([task.id, date, reason.trim(), name.trim()])),
                );
                key.reset();
              } catch (err) {
                setSaveError(err instanceof Error ? err.message : String(err));
                return;
              }
              await onDone();
            }}
          >
            Send request
          </ActionButton>
        </div>
    </Modal>
  );
}

function DeclineMoreTime({
  task,
  user,
  onClose,
  onDone,
}: {
  task: ServerTask;
  user: AppUser;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const lock = useActionLock();
  const [key] = useState(() => new SubmissionKey('cmt'));
  return (
    <Modal onClose={onClose} busy={lock.busy} dirty={reason.trim() !== ''}>
        <h2>Decline the request</h2>
        <p>{task.title}</p>
        <label style={{ display: 'block' }}>
          Why? (required — the store is emailed this)
          <textarea
            className={`q-input${tried && !reason.trim() ? ' input-required' : ''}`}
            rows={3}
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {tried && !reason.trim() && (
            <span className="error" style={{ fontSize: '0.85rem' }}>
              Say why you are declining.
            </span>
          )}
        </label>
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          The due date stays {task.dueDate ? formatDay(task.dueDate) : 'as it is'}.
        </p>
        {saveError && (
          <p className="error" role="alert">
            {saveError}
          </p>
        )}
        <div className="modal-actions">
          <button className="btn" disabled={lock.busy} onClick={onClose}>
            Cancel
          </button>
          <ActionButton
            className="btn btn-primary"
            lock={lock}
            busyLabel="Declining…"
            onAction={async () => {
              setSaveError(null);
              if (!reason.trim()) {
                setTried(true);
                return;
              }
              try {
                await declineMoreTime(task, reason, { upn: user.upn, name: user.displayName }, key.for(`${task.id}\n${reason.trim()}`));
                key.reset();
              } catch (err) {
                setSaveError(err instanceof Error ? err.message : String(err));
                return;
              }
              await onDone();
            }}
          >
            Decline
          </ActionButton>
        </div>
    </Modal>
  );
}

/** "today" or "tomorrow": the store's undo runs to the end of the day after completing (Eastern days). */
function undoLastDay(completedAt: string | null): string {
  return completedAt && daysUntil(completedAt, new Date()) < 0 ? 'today' : 'tomorrow';
}

/**
 * B5 (owner rulings 2026-10-25): every reminder, escalation, "assigned" and
 * reopen email sent about this task, plus what Flow 3 sends next. Server
 * mode only — hidden in direct (rollback) mode; a failed read is a quiet note.
 */
function TaskReminders({ task, storeActive }: { task: ServerTask; storeActive: boolean }) {
  const [view, setView] = useState<ReminderView | null | 'hidden'>(() => (dataViaApi() ? null : 'hidden'));
  const [failed, setFailed] = useState(false);
  // Back online → read it again (UI review 3, S3: "Couldn't load" stayed after the signal returned).
  const [tries, setTries] = useState(0);
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
  useEffect(() => {
    const on = () => {
      setOffline(false);
      setTries((n) => n + 1);
    };
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  useEffect(() => {
    if (!dataViaApi()) return;
    let live = true;
    setFailed(false);
    getTaskReminders(task.id)
      .then((v) => {
        if (live) setView(v ?? 'hidden');
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [task.id, tries]);
  if (view === 'hidden') return null;
  const loaded = view && !failed ? view : null;
  const next =
    // The flows act on active stores only.
    loaded && task.status !== 'complete' && storeActive
      ? loaded.rules.paused
        ? 'Notifications are paused.'
        : taskStepsText(
            nextTaskSteps(
              { dueDate: task.dueDate, status: task.status, lastOverdueReminderAt: task.lastOverdueReminderAt ?? null },
              loaded.rules,
              new Date(),
            ),
            loaded.dmName,
          )
      : '';
  return (
    <section className="task-reminders">
      <h2 style={{ marginTop: '1.5rem' }}>Reminders</h2>
      {next && <p className="muted reminder-next">{next}</p>}
      {failed && offline ? (
        <p className="muted">The reminder history needs signal — it loads when you’re back online.</p>
      ) : (
        <ReminderHistory entries={loaded?.entries ?? null} failed={failed} emptyText="No reminders or escalations sent for this task yet." />
      )}
    </section>
  );
}

/** Uploaded files, display-only — uploading lives in the composer below. */
function FilesSection({ files }: { files: TaskAttachmentRow[] | null }) {
  return (
    <>
      <h2 style={{ marginTop: '1.5rem' }}>Files</h2>
      {files === null && <p>Loading…</p>}
      {files !== null && files.length === 0 && (
        <p className="muted">No files on this task yet.</p>
      )}
      {files !== null && files.length > 0 && (
        <div className="attach-list">
          {files.map((f) => (
            <AttachmentChip
              key={f.id}
              kind={f.kind}
              filename={f.filename}
              url={f.url}
              download={() => downloadAuditAttachment(f.id)}
            />
          ))}
        </div>
      )}
    </>
  );
}

interface NotifyPerson {
  upn: string;
  name: string;
}

/** Chronological comments + system events, with the composer at the end. */
function ThreadSection({
  task,
  store,
  user,
  creatorName,
  thread,
  files,
  onPosted,
  onTaskChanged,
  onUploaded,
}: {
  task: ServerTask;
  store: Store | null;
  user: AppUser;
  /** Resolved display name of the task's creator ('' while resolving). */
  creatorName: string;
  thread: TaskThreadEntry[] | null;
  /** The task's files — "proof required" checks them before completing (B1). */
  files: TaskAttachmentRow[] | null;
  onPosted: () => void;
  onTaskChanged: () => void;
  onUploaded: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [body, setBody] = useState('');
  // Only a SHARED store account types who is writing; everyone else signs
  // with their own name (the server enforces this — writePolicy).
  const sharedAccount = user.role === 'StoreTeam';
  // A store account starts BLANK (owner ruling 2026-10-22: pre-filled with
  // the account's own name, comments went out as "the store", no person) and
  // is blanked again after every post, completion and upload (owner ruling
  // 2026-10-26: the next person on a shared device must never inherit it).
  const [name, setName] = useState('');
  const nameOk = !sharedAccount || isPersonName(name, user);
  const authorName = sharedAccount ? name.trim() : user.displayName;
  // A completion that went through but whose notes comment didn't post: a
  // retry must only post the comment, not complete (and log) it again.
  const completedHere = useRef(false);
  // Only meaningful for the completion in hand: a status change (another tab, an undo) starts afresh (QA).
  useEffect(() => {
    completedHere.current = false;
  }, [task.status, task.completedAt]);
  const [notify, setNotify] = useState<NotifyPerson[]>([]);
  const [picked, setPicked] = useState<NotifyPerson>({ upn: '', name: '' });
  // Remount key: PrincipalField keeps its own text, so clear by remounting.
  const [pickerKey, setPickerKey] = useState(0);
  const [addHint, setAddHint] = useState<string | null>(null);
  // Outside addresses an admin allowed (owner rulings 2026-10-19); empty until loaded = company only.
  const [allowlist, setAllowlist] = useState<EmailAllowlist>(EMPTY_ALLOWLIST);
  useEffect(() => {
    void getEmailAllowlist().then(setAllowlist);
  }, []);
  // "Task completed?" — 'yes' turns the post into complete-with-comment.
  const [completeChoice, setCompleteChoice] = useState<'no' | 'yes'>('no');
  // One lock for the composer: an upload and a post never run together,
  // and a double tap can't post (and email) a comment twice.
  const lock = useActionLock();
  const [postError, setPostError] = useState<string | null>(null);
  // The "you're offline" refusal goes once the signal is back (UI review 3, S3 — it stayed 35 s after).
  useEffect(() => {
    const on = () => setPostError((e) => (e === OFFLINE_COMPLETE_MESSAGE ? null : e));
    window.addEventListener('online', on);
    return () => window.removeEventListener('online', on);
  }, []);
  // "Saved on this device — sends when you have signal" after a queued post/upload (B4).
  const [queuedNote, setQueuedNote] = useState<string | null>(null);
  // A queued item for this task went out (B4): show it in Activity / Files.
  const { items: outboxItems } = useTaskOutbox();
  const waitingHere = outboxItems.filter((i) => i.taskId === task.id && i.state === 'waiting').length;
  const waitingBefore = useRef(waitingHere);
  useEffect(() => {
    if (waitingHere < waitingBefore.current) {
      onPosted();
      onUploaded();
      if (waitingHere === 0) setQueuedNote(null);
    }
    waitingBefore.current = waitingHere;
  }, [waitingHere]); // reload only when the count drops
  // Required-field errors show only after a Post / Upload try (final run:
  // the red "required" showed before anyone had typed anything).
  const [tried, setTried] = useState(false);
  // A file picked before the name was typed (store final run: Upload was
  // greyed with no clear reason). Held here — never sent until a name is in.
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  // A file uploaded here counts as proof at once — the Files list reloads
  // without waiting (QA 2026-10-25). Reset when the proof clock restarts.
  const [uploadedHere, setUploadedHere] = useState(false);
  useEffect(() => setUploadedHere(false), [task.proofSince]);
  const askName = () => {
    setTried(true);
    setPostError(NAME_REQUIRED_MESSAGE);
  };
  // One key per comment (src/data/clientKey.ts): a retry of the same text
  // after a lost answer is answered with the comment that landed — never a
  // second one (nor a second Flow 2c email).
  const [commentKey] = useState(() => new SubmissionKey('cmt'));
  const [completeKey] = useState(() => new SubmissionKey('ev'));

  const isOpen = task.status !== 'complete';
  const completing = isOpen && completeChoice === 'yes';
  // Completing a REOPENED task: quote why it was reopened (owner ruling 2026-10-22).
  const reopen = useMemo(() => (task.status === 'reopened' ? latestReopen(thread ?? []) : null), [task.status, thread]);
  // A completed task shows a clear "done" state: the composer folds behind
  // "Add a follow-up comment" (store round 2026-10-21).
  const [followUp, setFollowUp] = useState(false);
  // Fold it again whenever the task changes state (reopened, completed again) —
  // except right after a completion whose comment didn't post: its text stays
  // open in the follow-up box (QA, UI review 3 D1).
  const followUpAfterChange = useRef(false);
  useEffect(() => {
    setFollowUp(followUpAfterChange.current);
    followUpAfterChange.current = false;
  }, [task.status]);

  const hasNotify = (upn: string) =>
    notify.some((n) => equalsIgnoreCase(n.upn, upn));
  const toggleNotify = (person: NotifyPerson) => {
    setNotify((current) =>
      current.some((n) => equalsIgnoreCase(n.upn, person.upn))
        ? current.filter((n) => !equalsIgnoreCase(n.upn, person.upn))
        : [...current, person],
    );
  };
  const addPicked = () => {
    const upn = picked.upn.trim();
    if (upn && isAutoRecipient(upn)) {
      setAddHint(`${picked.name || upn} already gets this comment automatically.`);
    } else if (upn && !isAllowedEmail(upn, allowlist)) {
      // Company addresses + the admin allowlist only (G2; the server and the flows enforce it too).
      setAddHint(`${picked.name || upn} can’t be emailed — only ${allowedLabel(allowlist)}.`);
    } else if (upn && !hasNotify(upn) && extraNotify.length >= MAX_NOTIFY_EXTRA) {
      setAddHint(`At most ${MAX_NOTIFY_EXTRA} extra people per comment.`);
    } else if (upn && !hasNotify(upn)) {
      setNotify((current) => [...current, { upn, name: picked.name || upn }]);
      setAddHint(null);
    }
    setPicked({ upn: '', name: '' });
    setPickerKey((k) => k + 1);
  };

  // Quick-selects: the store's DM and the task's creator (when known).
  // Only offered for company addresses (G2 — the server would refuse others).
  // Never offer to email the viewer themselves (DM round 2026-10-21: the DM
  // saw "Also email the DM — <themselves>").
  const dmQuick: NotifyPerson | null =
    store && isInternalEmail(store.dmUpn) && !equalsIgnoreCase(store.dmUpn, user.upn)
      ? { upn: store.dmUpn.trim(), name: store.dmName.trim() || store.dmUpn.trim() }
      : null;
  const creatorQuick: NotifyPerson | null =
    isInternalEmail(task.createdBy) && !equalsIgnoreCase(task.createdBy, user.upn)
    ? { upn: task.createdBy.trim(), name: creatorName || task.createdBy.trim() }
    : null;

  // Flow 2c's default recipient: the assignee — or the creator when the
  // assignee is the one commenting. Checkboxes for that person are hidden
  // (they'd be redundant), and the line above the list names them.
  const assigneeLabel = usePersonLabel(task.assignedTo);
  const creatorLabel = usePersonLabel(task.createdBy);
  const userIsAssignee = equalsIgnoreCase(task.assignedTo, user.upn);
  const autoRecipientUpn = userIsAssignee ? task.createdBy : task.assignedTo;
  const autoRecipientLabel = userIsAssignee
    ? `${creatorName || creatorLabel} (task creator)`
    : `${assigneeLabel} (assignee)`;
  const isAutoRecipient = (upn: string) =>
    equalsIgnoreCase(upn, autoRecipientUpn);
  // Never carry the automatic recipient as an extra: their checkbox is
  // hidden, so a stale pick (e.g. checked the DM, then reassigned the task
  // to them on this page) would otherwise be invisible and un-removable.
  const extraNotify = notify.filter((n) => !isAutoRecipient(n.upn));

  /**
   * 'sent' once the file is on the task; 'queued' when there was no signal
   * and it's saved on this device to send later (B4); false when held or failed.
   */
  const onPickFile = async (file: File): Promise<'sent' | 'queued' | false> => {
    // The name box can change while the file picker is open (QA 2026-10-22):
    // a store account needs a person's name before anything goes out.
    if (!nameOk) {
      setPendingFile(file);
      askName();
      if (inputRef.current) inputRef.current.value = '';
      return false;
    }
    setUploading(true);
    setPostError(null);
    setQueuedNote(null);
    // One key per upload: a resend (here or from the device queue) returns the same file.
    const uploadKey = newClientKey('tfile');
    const eventKey = newClientKey('file');
    let prepared: PreparedTaskFile;
    try {
      prepared = await prepareTaskFile(file);
    } catch (err) {
      setPostError(err instanceof Error ? err.message : String(err));
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
      return false;
    }
    // No signal (B4, owner rulings 2026-10-25): kept on this device, sent when it returns.
    const queue = async (afterFailedTry = false): Promise<'queued' | false> => {
      try {
        await getTaskOutbox().enqueueFile({
          taskId: task.id,
          storeId: task.storeId,
          taskTitle: task.title,
          authorName,
          fileKind: prepared.kind,
          fileName: prepared.sourceName,
          fileTitle: prepared.title,
          blob: prepared.blob,
          key: uploadKey,
          eventKey,
          afterFailedTry,
        });
        setQueuedNote(QUEUED_NOTE);
        return 'queued';
      } catch {
        setPostError("You’re offline, and this device couldn’t save the file to send later. Try again when you have signal.");
        return false;
      }
    };
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return await queue();
      const filename = await uploadPreparedTaskFile(task, prepared, user.upn, uploadKey);
      // Timeline row is best-effort — the upload itself already succeeded.
      // Signed like a comment: a shared store account's typed name (store
      // round 2026-10-21: the event named the account, the comments the person).
      // One key for this upload's event (generated now): a request the
      // browser re-sends never adds a second "Added …" row. Never retried
      // by hand — the row is best-effort — so no SubmissionKey is needed.
      await addTaskEvent(
        task.id,
        'file',
        `Added ${filename}`,
        user.upn,
        authorName,
        '',
        eventKey,
      ).catch(() => {});
      setUploadedHere(true);
      onUploaded();
      return 'sent';
    } catch (err) {
      // It may have landed: the queued resend carries the same key, so it can't make a second copy.
      if (err instanceof OfflineError) return await queue(true);
      // Say WHICH action failed (round 4: an offline upload showed the comment's message).
      setPostError(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  /**
   * An upload on its own (not the file a Post sends first): once the file is
   * on the task or saved to send, a shared account's typed name is blanked
   * (owner ruling 2026-10-26) — unless a comment is being written (text, a
   * "Yes — complete" choice or people to email): that is the same person
   * mid-action (e.g. the photo a proof-required completion needs), so the
   * name stays until the comment or completion is sent (his follow-up
   * ruling, same day). A Post keeps it until the comment is done.
   */
  const uploadAlone = async (file: File) => {
    const drafting = body.trim() !== '' || completeChoice === 'yes' || notify.length > 0;
    const result = await onPickFile(file);
    if (result && sharedAccount && !drafting) {
      setName('');
      setTried(false);
    }
    return result;
  };

  const post = async () => {
    setPostError(null);
    if (!nameOk) {
      askName();
      return;
    }
    if (body.trim() === '') {
      setTried(true);
      return;
    }
    // "Proof required" (B1, 2026-10-25): no photo/PDF since the task was made
    // or reopened → say so BEFORE posting the comment (the server refuses the
    // completion too). A held file counts — it goes up first, below.
    if (completing && task.proofRequired && !pendingFile && !uploadedHere && proofMissing(task, files)) {
      // Their photo is saved on the device and hasn't gone out yet (QA 2026-10-25).
      const fileWaiting = outboxItems.some((i) => i.taskId === task.id && i.kind === 'file' && i.state === 'waiting');
      setPostError(fileWaiting ? 'Your photo/PDF is still sending from this device — try again once it shows under Files.' : PROOF_REQUIRED_MESSAGE);
      return;
    }
    // A DM completing the store's own task: ask first (owner ruling 2026-10-25).
    // Store not loaded yet → read it (cached) rather than skip the question (QA 2026-10-25).
    if (completing) {
      const taskStore =
        store ??
        (await getVisibleStores(user, { includeInactive: true })
          .then((all) => all.find((st) => Number(st.id) === task.storeId) ?? null)
          .catch(() => null));
      if (completesOnStoreBehalf(task, taskStore, user) && !window.confirm(ON_BEHALF_CONFIRM)) return;
    }
    // A file picked before the name was typed goes up first — never left
    // behind by the comment or completion (QA 2026-10-25).
    if (pendingFile) {
      const result = await onPickFile(pendingFile);
      if (!result) return;
      setPendingFile(null);
      // Completing needs signal (owner ruling 2026-10-25); the file waits on the device.
      if (result === 'queued' && completing) {
        setPostError(OFFLINE_COMPLETE_MESSAGE);
        return;
      }
    }
    const offlineNow = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (offlineNow && completing) {
      setPostError(OFFLINE_COMPLETE_MESSAGE);
      return;
    }
    // Recipients and the typed name are part of WHAT is sent: changing them is a new comment.
    const notifyUpns = extraNotify.map((n) => n.upn);
    const key = commentKey.for(JSON.stringify([task.id, body.trim(), authorName, extraNotify.map((n) => n.upn.toLowerCase()).sort()]));
    const clearForm = () => {
      if (sharedAccount) setName('');
      setBody('');
      setNotify([]);
      setAddHint(null);
      setCompleteChoice('no');
      setTried(false);
    };
    // A plain comment with no signal (B4): kept on this device, sent when it returns.
    const queueComment = async (afterFailedTry = false): Promise<boolean> => {
      try {
        await getTaskOutbox().enqueueComment({
          taskId: task.id,
          storeId: task.storeId,
          taskTitle: task.title,
          body: body.trim(),
          authorName,
          notifyUpns,
          clientKey: key,
          afterFailedTry,
        });
      } catch {
        return false;
      }
      commentKey.reset();
      clearForm();
      setQueuedNote(QUEUED_NOTE);
      return true;
    };
    if (offlineNow && !completing) {
      if (!(await queueComment())) setPostError("You’re offline, and this device couldn’t save the comment to send later. Try again when you have signal.");
      return;
    }
    try {
      // Complete FIRST, then post the notes as the comment (UI review 3, D1): a
      // completion the server refuses (no proof since the reopen) must leave no
      // "Replaced the cover." comment behind saying the job was done.
      // This submit's own record of it (QA: the ref can be reset by a refresh while the comment posts).
      let completionLanded = completing && completedHere.current;
      if (completing && !completedHere.current) {
        await completeTask(task.id, authorName, body.trim(), user.upn, true, completeKey.for(`${task.id}\n${body.trim()}`));
        completeKey.reset();
        completedHere.current = true;
        completionLanded = true;
      }
      try {
        await addTaskComment(task.id, body.trim(), user.upn, authorName, notifyUpns, key);
      } catch (err) {
        // No answer: it may have landed — the queued resend carries the same key (no second comment or email).
        if (err instanceof OfflineError && !completing && (await queueComment(true))) return;
        if (completionLanded) {
          // The task IS complete; only its comment is missing (QA): never say "completing needs signal".
          completedHere.current = false;
          if (err instanceof OfflineError && (await queueComment(true))) {
            onTaskChanged();
            return;
          }
          // Keep the text in a follow-up box so one tap sends it (the page now shows the task completed).
          followUpAfterChange.current = true;
          setFollowUp(true);
          setPostError('Marked complete — but your comment didn’t post. Tap Post to send it.');
          onTaskChanged();
          return;
        }
        throw err;
      }
      commentKey.reset();
      completedHere.current = false;
      clearForm();
      setQueuedNote(null);
      onPosted();
      if (completing) onTaskChanged();
    } catch (err) {
      const message = completing && err instanceof OfflineError ? OFFLINE_COMPLETE_MESSAGE : err instanceof Error ? err.message : String(err);
      setPostError(message);
      // Refused for proof (e.g. a DM switched it on after this page loaded):
      // re-read the task so the notice shows.
      if (message === PROOF_REQUIRED_MESSAGE) void onTaskChanged();
    }
  };

  return (
    <>
      {/* Final layout (owner 2026-09-16): composer right under Files, then
          the Activity heading + feed, newest first, as the bottom-most
          section. */}
      {/* Executives are read-only (the server refuses their writes too). */}
      {user.role !== 'Executive' && !isOpen && !followUp && (
        <p style={{ marginTop: '1rem' }}>
          <button type="button" className="btn" onClick={() => setFollowUp(true)}>
            Add a follow-up comment
          </button>
        </p>
      )}
      {user.role !== 'Executive' && (isOpen || followUp) && (
        <div className="thread-composer">
          {isOpen && task.proofRequired && (
            // Says when the proof is in (UI review U192 — it kept asking after the upload).
            <p className={uploadedHere || (files !== null && !proofMissing(task, files)) ? 'proof-notice proof-ok' : 'proof-notice'}>
              {uploadedHere || (files !== null && !proofMissing(task, files))
                ? '✓ Photo or PDF added — you can complete this task.'
                : '📎 Proof required — upload a photo or PDF below before you mark it complete.'}
            </p>
          )}
          <div className="form-grid">
            {isOpen && (
              <label>
                Task completed?
                <select
                  value={completeChoice}
                  onChange={(e) =>
                    setCompleteChoice(e.target.value === 'yes' ? 'yes' : 'no')
                  }
                >
                  <option value="no">No — just a comment</option>
                  <option value="yes">Yes — complete this task</option>
                </select>
              </label>
            )}
            {sharedAccount && (
              <label>
                Your name
                <input
                  value={name}
                  maxLength={100}
                  required
                  className={nameOk || !tried ? undefined : 'input-required'}
                  onChange={(e) => {
                    setName(e.target.value);
                    // A leftover "type your name" error goes once a name is typed (QA 2026-10-24).
                    setPostError((prev) => (prev === NAME_REQUIRED_MESSAGE ? null : prev));
                  }}
                  autoComplete="off"
                  placeholder="First and last name"
                />
                {!nameOk && tried && (
                  <span className="error" style={{ fontSize: '0.85rem' }}>
                    {NAME_REQUIRED_MESSAGE}
                  </span>
                )}
              </label>
            )}
            {completing && reopen && <ReopenReason note={reopen} className="span-2" />}
            <label className="span-2">
              {completing ? 'Completion comment (required)' : 'Add a comment'}
              <textarea
                className={
                  tried && body.trim() === ''
                    ? 'q-input textarea-required'
                    : 'q-input'
                }
                rows={3}
                maxLength={10_000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={
                  completing
                    ? 'What did you do? Add a photo if you can.'
                    : 'Progress update, question, what was done…'
                }
              />
              {tried && body.trim() === '' && (
                <span className="error" style={{ fontSize: '0.85rem' }}>
                  {completing ? 'A completion comment is required.' : 'Type a comment first.'}
                </span>
              )}
            </label>
          </div>
          {sharedAccount && (
            <p className="muted" style={{ fontSize: '0.85rem', margin: '0.25rem 0' }}>
              Store accounts are shared — the name records who actually wrote the
              comment.
            </p>
          )}

          <div className="notify-block">
            <span className="muted notify-label">Email notifications</span>
            <ul className="notify-options">
              <li className="notify-auto muted">
                ✉ {autoRecipientLabel} — gets this comment automatically
              </li>
              {dmQuick && !isAutoRecipient(dmQuick.upn) && (
                <li>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={hasNotify(dmQuick.upn)}
                      disabled={!hasNotify(dmQuick.upn) && extraNotify.length >= MAX_NOTIFY_EXTRA}
                      onChange={() => toggleNotify(dmQuick)}
                    />
                    Also email the DM — {dmQuick.name}
                  </label>
                </li>
              )}
              {creatorQuick && !isAutoRecipient(creatorQuick.upn) && (
                <li>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={hasNotify(creatorQuick.upn)}
                      disabled={!hasNotify(creatorQuick.upn) && extraNotify.length >= MAX_NOTIFY_EXTRA}
                      onChange={() => toggleNotify(creatorQuick)}
                    />
                    Also email the task creator — {creatorQuick.name}
                  </label>
                </li>
              )}
              {extraNotify
                .filter(
                  (n) =>
                    !(dmQuick && equalsIgnoreCase(n.upn, dmQuick.upn)) &&
                    !(creatorQuick && equalsIgnoreCase(n.upn, creatorQuick.upn)),
                )
                .map((n) => (
                  <li key={n.upn}>
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked
                        onChange={() => toggleNotify(n)}
                      />
                      Also email {n.name}
                    </label>
                  </li>
                ))}
            </ul>
            <div className="notify-picker">
              <PrincipalField
                key={pickerKey}
                type="user"
                label="Also email someone else (search by name)"
                value={picked.upn}
                onChange={({ value, displayName }) =>
                  setPicked({ upn: value, name: displayName ?? value })
                }
              />
              <button
                type="button"
                className="btn"
                disabled={picked.upn.trim() === ''}
                onClick={addPicked}
              >
                Add
              </button>
            </div>
            {addHint && (
              <p className="muted" style={{ fontSize: '0.85rem', margin: '0.25rem 0 0' }}>
                {addHint}
              </p>
            )}
          </div>

          {/* The name rule already shows under the name box — never twice (round 4). */}
          {postError && !(postError === NAME_REQUIRED_MESSAGE && !nameOk) && <p className="error">{postError}</p>}
          {queuedNote && !postError && <p className="queued-note">{queuedNote}</p>}
          {pendingFile && (
            <p className="pending-upload">
              📎 {pendingFile.name} —{' '}
              {nameOk ? 'ready to upload' : 'type your name above, then upload it'}{' '}
              <ActionButton
                className="btn btn-small"
                lock={lock}
                busyLabel="Uploading…"
                onAction={async () => {
                  if (!nameOk) {
                    askName();
                    return;
                  }
                  // Cleared once it's on the task or saved to send — a failed upload keeps it (QA 2026-10-25).
                  if (await uploadAlone(pendingFile)) setPendingFile(null);
                }}
              >
                Upload
              </ActionButton>{' '}
              <button type="button" className="btn btn-small" disabled={lock.busy} onClick={() => setPendingFile(null)}>
                Remove
              </button>
            </p>
          )}
          <div className="modal-actions">
            <input
              ref={inputRef}
              type="file"
              accept="image/*,application/pdf,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (lock.locked) {
                  e.target.value = '';
                  return;
                }
                void lock.run(() => uploadAlone(file));
              }}
            />
            <button
              className="btn"
              // The file can be picked first; it's held until a name is typed
              // (the upload's timeline row is signed with it).
              disabled={uploading || lock.busy}
              title={nameOk ? undefined : 'You can pick it now — it uploads once your name is typed'}
              onClick={() => inputRef.current?.click()}
            >
              {uploading ? 'Uploading…' : '+ Upload photo / PDF'}
            </button>
            <ActionButton
              className="btn btn-primary"
              lock={lock}
              busyLabel={completing ? 'Completing…' : 'Posting…'}
              onAction={post}
            >
              {completing ? 'Complete task' : 'Post comment'}
            </ActionButton>
          </div>
        </div>
      )}

      {/* Comments/files waiting on this device (B4) — shown even once the composer folds. */}
      <OutboxList taskId={task.id} />
      <h2 style={{ marginTop: '1.5rem' }}>Activity</h2>
      {thread === null && <p>Loading…</p>}
      {thread !== null && thread.length === 0 && (
        <p className="muted">
          {user.role === 'Executive' ? 'No activity yet.' : 'Nothing here yet — add the first comment above.'}
        </p>
      )}
      {thread !== null &&
        [...thread].reverse().map((entry) =>
          entry.entryType === 'event' ? (
            <div key={entry.id} className="thread-event muted">
              {EVENT_ICONS[entry.eventKind] ?? '•'} <EventText body={entry.body} eventKind={entry.eventKind} />
              {' — '}
              <AuthorLabel name={entry.authorName} upn={entry.authorUpn} />,{' '}
              {formatWhen(entry.createdAt)}
            </div>
          ) : (
            <div key={entry.id} className="thread-comment">
              <div className="muted thread-comment-head">
                <strong>
                  <AuthorLabel name={entry.authorName} upn={entry.authorUpn} />
                </strong>{' '}
                · {formatWhen(entry.createdAt)}
              </div>
              <div className="thread-comment-body">{entry.body}</div>
              {entry.notifyUpns.length > 0 && (
                <div className="muted thread-comment-notify">
                  🔔 Also notified:{' '}
                  {entry.notifyUpns.map((u, i) => (
                    <span key={u}>
                      {i > 0 && ', '}
                      <PersonName upn={u} />
                    </span>
                  ))}
                </div>
              )}
            </div>
          ),
        )}
    </>
  );
}

const EVENT_ICONS: Record<string, string> = {
  completed: '✅',
  reopened: '↩️',
  reassigned: '👤',
  due_date: '📅',
  file: '📎',
};

/** When it happened — company time, labelled ET (2026-10-28). */
function formatWhen(iso: string): string {
  return formatDateTime(iso);
}

/**
 * Who wrote an entry: the typed name, plus the signed-in account whenever it
 * differs — a shared store account types who is writing, and the account
 * (stamped by the server) is what can't be made up. Same as Flow 2c's email.
 */
function AuthorLabel({ name, upn }: { name: string; upn: string }) {
  // The account reads as the app shows it everywhere (ui/people): a store's
  // shared account as "90007 — Store name", a person by display name — never
  // the raw mailbox (DM + store rounds 2026-10-21); the address stays in the
  // hover title. A typed name that differs shows as "typed (account)" (below).
  const account = usePersonLabel(upn);
  const typed = name.trim();
  if (!typed || typed.toLowerCase() === upn.toLowerCase() || typed.toLowerCase() === account.trim().toLowerCase()) {
    return <PersonName upn={upn} />;
  }
  // A person's own account, once resolved to their directory name: that
  // name alone (UI review U59 — "Robert Henry Jr (Robert Henry)" read as two
  // people; the server stamps a person's name from their sign-in anyway).
  // A store's shared account ALWAYS shows next to the typed name (security.md
  // "Comment author spoofing"), and so does any account not resolved yet or
  // not visible to this viewer — the typed name is never dropped for it.
  const isStore = /^\d+ — /.test(account);
  const resolved = account.trim() !== '' && account !== shortAddress(upn);
  if (resolved && !isStore) return <PersonName upn={upn} />;
  return (
    <>
      <bdi>{typed}</bdi>{' '}
      <span className="thread-author-account" title={upn.trim() || undefined}>
        ({account})
      </span>
    </>
  );
}

/**
 * No photo/PDF on the task since ProofSince (B1). Files whose upload time
 * isn't known count as proof (the server has the final say).
 */
function proofMissing(task: ServerTask, files: TaskAttachmentRow[] | null): boolean {
  if (files === null) return false;
  if (files.some((f) => !f.uploadedAt)) return files.length === 0;
  return !hasProofSince(
    files.map((f) => f.uploadedAt!),
    task.proofSince,
  );
}
